import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Nav } from '@/components/layout/Nav';
import { Footer } from '@/components/layout/Footer';
import { useReveal } from '@/hooks/useReveal';
import { useHashScroll } from '@/hooks/useHashScroll';
import { useScrollVars } from '@/hooks/useScrollVars';
import { usePointerVars } from '@/hooks/usePointerVars';
import { useLang } from '@/i18n/LangContext';

const Home = lazy(() => import('@/pages/Home'));
const Yacht = lazy(() => import('@/pages/Yacht'));
const Location = lazy(() => import('@/pages/Location'));
const Availability = lazy(() => import('@/pages/Availability'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Per-route nav transition. The nav has two visual modes (dark = navy bg /
// cream text, light = cream bg / navy text) and toggles at ~92 vh of scroll.
// Default routes open dark over the navy hero and switch light over the cream
// sections below. /yacht is the operator-preferred inverse: cream nav over
// the navy hero, navy nav below (operator 2026-05-24, "header at the start
// is cream then when scrolling under the hero section it becomes blue").
type NavMode = 'dark' | 'light';
const ROUTE_NAV_MODES: Record<string, { initialMode: NavMode; scrolledMode: NavMode }> = {
  '/':             { initialMode: 'dark',  scrolledMode: 'light' },
  '/yacht':        { initialMode: 'light', scrolledMode: 'dark'  },
  '/location':     { initialMode: 'dark',  scrolledMode: 'light' },
  '/availability': { initialMode: 'dark',  scrolledMode: 'light' },
  '/privacy':      { initialMode: 'light', scrolledMode: 'light' },
};
const DEFAULT_NAV_MODE = { initialMode: 'dark' as NavMode, scrolledMode: 'light' as NavMode };

export default function App() {
  const location = useLocation();
  const { hash } = location;
  // `basePath` is the path without its locale prefix: `/it/yacht` and `/yacht` are
  // the same page, so one route table serves both languages and a language switch
  // is not a page change (no scroll reset, no remount, same nav mode).
  const { lang, basePath, copy } = useLang();
  const routedLocation = useMemo(() => ({ ...location, pathname: basePath }), [location, basePath]);

  // Reset scroll on page change UNLESS a hash is present — useHashScroll
  // owns the destination scroll position in that case so we must not race it.
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [basePath, hash]);

  useHashScroll();
  useReveal(`${basePath}|${lang}`);
  useScrollVars();
  usePointerVars();

  const navMode = ROUTE_NAV_MODES[basePath] ?? DEFAULT_NAV_MODE;

  return (
    <>
      <a href="#main" className="skip-link">
        {copy.nav.skipToContent}
      </a>
      <Nav initialMode={navMode.initialMode} scrolledMode={navMode.scrolledMode} />
      <main id="main" tabIndex={-1}>
        <Suspense fallback={<div className="page-hero" aria-hidden="true" />}>
          <Routes location={routedLocation}>
            <Route path="/" element={<Home />} />
            <Route path="/yacht" element={<Yacht />} />
            <Route path="/location" element={<Location />} />
            <Route path="/availability" element={<Availability />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}
